import { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Generate random color
function getRandomColor() {
  const hues = [0, 30, 60, 120, 180, 220, 260, 300]; // Red, Orange, Yellow, Green, Cyan, Blue, Purple, Pink
  const hue = hues[Math.floor(Math.random() * hues.length)] + (Math.random() * 30 - 15);
  const saturation = 60 + Math.random() * 30; // 60-90%
  const lightness = 50 + Math.random() * 20; // 50-70%
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

// Create circle texture with variable blur and color
function createCircleTexture(blur = 0, color = null) {
  const canvas = document.createElement('canvas');
  const size = 128;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  const center = size / 2;
  const radius = size / 2 - 2;

  // Parse color or use default
  const circleColor = color || getRandomColor();
  const tempDiv = document.createElement('div');
  tempDiv.style.color = circleColor;
  document.body.appendChild(tempDiv);
  const computedColor = getComputedStyle(tempDiv).color;
  document.body.removeChild(tempDiv);

  // Extract RGB values
  const rgbMatch = computedColor.match(/\d+/g);
  const r = rgbMatch ? parseInt(rgbMatch[0]) : 150;
  const g = rgbMatch ? parseInt(rgbMatch[1]) : 150;
  const b = rgbMatch ? parseInt(rgbMatch[2]) : 150;

  if (blur > 0) {
    // Blurry circle - gradient from center
    const gradient = ctx.createRadialGradient(center, center, 0, center, center, radius);
    const blurFactor = Math.min(blur, 1);
    gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${0.6 - blurFactor * 0.3})`);
    gradient.addColorStop(0.3 * (1 - blurFactor * 0.5), `rgba(${r}, ${g}, ${b}, ${0.5 - blurFactor * 0.2})`);
    gradient.addColorStop(0.7, `rgba(${r}, ${g}, ${b}, ${0.2 - blurFactor * 0.1})`);
    gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  } else {
    // Sharp circle
    ctx.beginPath();
    ctx.arc(center, center, radius * 0.9, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.8)`;
    ctx.fill();
  }

  return new THREE.CanvasTexture(canvas);
}

// Individual floating circle with depth-based blur
function FloatingCircle({ position, size, blur, speed, phase, color }) {
  const meshRef = useRef();
  const texture = useMemo(() => createCircleTexture(blur, color), [blur, color]);

  useFrame((state) => {
    if (meshRef.current) {
      const t = state.clock.elapsedTime;
      // Gentle floating motion
      meshRef.current.position.x = position[0] + Math.sin(t * speed + phase) * 0.3;
      meshRef.current.position.y = position[1] + Math.cos(t * speed * 0.7 + phase) * 0.2;
    }
  });

  return (
    <mesh ref={meshRef} position={position}>
      <planeGeometry args={[size, size]} />
      <meshBasicMaterial
        map={texture}
        transparent
        depthWrite={false}
        blending={THREE.NormalBlending}
      />
    </mesh>
  );
}

// Floating particles/dots (small sharp circles)
function FloatingDots() {
  const count = 150;
  const dotsRef = useRef();

  const [positions, speeds, phases] = useMemo(() => {
    const pos = [];
    const spd = [];
    const phs = [];

    for (let i = 0; i < count; i++) {
      pos.push([
        (Math.random() - 0.5) * 14,
        (Math.random() - 0.5) * 10,
        -2 + Math.random() * 4 // z depth
      ]);
      spd.push(0.1 + Math.random() * 0.3);
      phs.push(Math.random() * Math.PI * 2);
    }

    return [pos, spd, phs];
  }, []);

  const positionsArray = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = positions[i][0];
      arr[i * 3 + 1] = positions[i][1];
      arr[i * 3 + 2] = positions[i][2];
    }
    return arr;
  }, [positions]);

  const sizesArray = useMemo(() => {
    const arr = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      // Size based on z-depth: closer = bigger
      const depth = (positions[i][2] + 2) / 4;
      arr[i] = 0.02 + depth * 0.06;
    }
    return arr;
  }, [positions]);

  const opacitiesArray = useMemo(() => {
    const arr = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const depth = (positions[i][2] + 2) / 4;
      arr[i] = 0.3 + depth * 0.7;
    }
    return arr;
  }, [positions]);

  const colorsArray = useMemo(() => {
    const arr = new Float32Array(count * 3);
    const hues = [0, 30, 60, 120, 180, 220, 260, 300];
    for (let i = 0; i < count; i++) {
      const hue = hues[Math.floor(Math.random() * hues.length)] + (Math.random() * 30 - 15);
      const color = new THREE.Color(`hsl(${hue}, ${70 + Math.random() * 20}%, ${55 + Math.random() * 15}%)`);
      arr[i * 3] = color.r;
      arr[i * 3 + 1] = color.g;
      arr[i * 3 + 2] = color.b;
    }
    return arr;
  }, []);

  const vertexShader = `
    attribute float size;
    attribute float opacity;
    attribute vec3 color;
    varying float vOpacity;
    varying vec3 vColor;

    void main() {
      vOpacity = opacity;
      vColor = color;
      vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
      gl_PointSize = size * (300.0 / -mvPosition.z);
      gl_Position = projectionMatrix * mvPosition;
    }
  `;

  const fragmentShader = `
    varying float vOpacity;
    varying vec3 vColor;

    void main() {
      vec2 center = gl_PointCoord - vec2(0.5);
      float dist = length(center);
      if (dist > 0.5) discard;

      float alpha = smoothstep(0.5, 0.2, dist) * vOpacity;

      gl_FragColor = vec4(vColor, alpha);
    }
  `;

  useFrame((state) => {
    if (dotsRef.current) {
      const positions = dotsRef.current.geometry.attributes.position.array;
      const time = state.clock.elapsedTime;

      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        positions[i3] += Math.sin(time * speeds[i] + phases[i]) * 0.002;
        positions[i3 + 1] += Math.cos(time * speeds[i] * 0.8 + phases[i]) * 0.0015;
      }

      dotsRef.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  return (
    <points ref={dotsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positionsArray}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-size"
          count={count}
          array={sizesArray}
          itemSize={1}
        />
        <bufferAttribute
          attach="attributes-opacity"
          count={count}
          array={opacitiesArray}
          itemSize={1}
        />
        <bufferAttribute
          attach="attributes-color"
          count={count}
          array={colorsArray}
          itemSize={3}
        />
      </bufferGeometry>
      <shaderMaterial
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        transparent
        depthWrite={false}
      />
    </points>
  );
}

// Main background scene with depth-of-field circles
function BackgroundScene() {
  // Generate circles with varying depths and blur
  const circles = useMemo(() => {
    const items = [];

    // Very large, very blurry circles (far background)
    for (let i = 0; i < 6; i++) {
      items.push({
        position: [
          (Math.random() - 0.5) * 12,
          (Math.random() - 0.5) * 8,
          -3 - Math.random() * 2
        ],
        size: 3 + Math.random() * 4,
        blur: 0.8 + Math.random() * 0.2,
        speed: 0.05 + Math.random() * 0.1,
        phase: Math.random() * Math.PI * 2,
        color: getRandomColor()
      });
    }

    // Medium blurry circles (mid-far)
    for (let i = 0; i < 10; i++) {
      items.push({
        position: [
          (Math.random() - 0.5) * 10,
          (Math.random() - 0.5) * 7,
          -2 - Math.random() * 1
        ],
        size: 1.5 + Math.random() * 2,
        blur: 0.5 + Math.random() * 0.3,
        speed: 0.1 + Math.random() * 0.15,
        phase: Math.random() * Math.PI * 2,
        color: getRandomColor()
      });
    }

    // Medium circles (mid-ground)
    for (let i = 0; i < 15; i++) {
      items.push({
        position: [
          (Math.random() - 0.5) * 8,
          (Math.random() - 0.5) * 6,
          -1 + Math.random() * 1
        ],
        size: 0.5 + Math.random() * 1,
        blur: 0.2 + Math.random() * 0.3,
        speed: 0.15 + Math.random() * 0.2,
        phase: Math.random() * Math.PI * 2,
        color: getRandomColor()
      });
    }

    // Small, sharp circles (foreground/focus) - varied sizes
    for (let i = 0; i < 10; i++) {
      items.push({
        position: [
          (Math.random() - 0.5) * 7,
          (Math.random() - 0.5) * 5,
          0 + Math.random() * 2
        ],
        size: 0.08 + Math.random() * 0.5,
        blur: 0,
        speed: 0.2 + Math.random() * 0.25,
        phase: Math.random() * Math.PI * 2,
        color: getRandomColor()
      });
    }

    return items;
  }, []);

  return (
    <>
      <color attach="background" args={['#1a1a2e']} />
      
      {/* Background circles */}
      {circles.map((circle, index) => (
        <FloatingCircle
          key={index}
          {...circle}
        />
      ))}
      
      {/* Foreground particles */}
      <FloatingDots />
    </>
  );
}

export function LoginBackground() {
  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: -1,
    }}>
      <Canvas
        camera={{ position: [0, 0, 5], fov: 50 }}
        gl={{ antialias: true, alpha: false }}
      >
        <BackgroundScene />
      </Canvas>
    </div>
  );
}
